<template>
  <div class="min-h-screen bg-[#F8F7FA] p-6">
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>
    <div class="space-y-6 sm:space-y-8">
      <!-- Header -->
      <div
        class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0"
      >
        <h1 class="text-2xl sm:text-3xl font-bold text-[#4A4453]">
          {{ $t("vendings.title") }}
        </h1>
        <button
          @click="getMachines"
          class="w-full sm:w-auto px-4 py-2 bg-[#FF5722] text-white rounded-xl hover:bg-opacity-90 transition-colors flex items-center justify-center sm:justify-start gap-2 font-medium"
        >
          <span class="material-icons">refresh</span>
          {{ $t("vendings.refresh") }}
        </button>
      </div>

      <!-- Stats -->
      <div
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8"
      >
        <StatsCard
          :title="$t('vendings.totalVendings')"
          :value="totalMachines"
          type="total"
        >
          <template #icon>
            <VendingIcon />
          </template>
        </StatsCard>

        <StatsCard
          :title="$t('vendings.filterOnline')"
          :value="activeMachines"
          type="active"
        >
          <template #icon>
            <span class="material-icons">check_circle</span>
          </template>
        </StatsCard>

        <StatsCard
          :title="$t('vendings.filterOffline')"
          :value="inactiveMachines"
          type="inactive"
        >
          <template #icon>
            <span class="material-icons">cancel</span>
          </template>
        </StatsCard>
      </div>

      <!-- Machines Section -->
      <div class="bg-white rounded-2xl shadow-sm p-4 sm:p-6 lg:p-8">
        <div
          class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-6"
        >
          <h2 class="text-xl sm:text-2xl font-semibold text-[#4A4453]">
            {{ $t("vendings.vendingMachines") }}
          </h2>
          <div class="flex gap-3 w-full sm:w-auto justify-end">
            <button
              :class="[
                'p-2.5 rounded-xl transition-colors flex-1 sm:flex-none',
                viewMode === 'grid'
                  ? 'bg-[#FF5722] text-white'
                  : 'bg-gray-100 text-[#4A4453] hover:bg-gray-200'
              ]"
              @click="viewMode = 'grid'"
            >
              <span class="material-icons">grid_view</span>
            </button>
            <button
              :class="[
                'p-2.5 rounded-xl transition-colors flex-1 sm:flex-none',
                viewMode === 'list'
                  ? 'bg-[#FF5722] text-white'
                  : 'bg-gray-100 text-[#4A4453] hover:bg-gray-200'
              ]"
              @click="viewMode = 'list'"
            >
              <span class="material-icons">view_list</span>
            </button>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row gap-4 mb-6">
          <div class="flex-1 w-full">
            <SearchBar
              :placeholder="$t('vendings.searchPlaceholder')"
              :value="searchQuery"
              @input="searchQuery = $event"
            />
          </div>
          <div class="w-full sm:w-auto overflow-x-auto">
            <FilterBar :filters="filters" @update:filters="filters = $event" />
          </div>
        </div>

        <!-- Conteúdo Principal -->
        <div class="min-h-[500px]">
          <!-- Grid View -->
          <template v-if="viewMode === 'grid'">
            <div
              class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            >
              <VendingMachineCard
                v-for="machine in paginatedMachines"
                :key="machine.id"
                v-bind="machine"
              />
            </div>
          </template>

          <!-- List View -->
          <template v-else>
            <!-- Cabeçalho da Lista (visível apenas em telas maiores) -->
            <div
              class="hidden sm:grid grid-cols-12 gap-4 items-center text-sm font-medium text-[#4A4453] bg-gray-50 p-4 rounded-xl mb-4"
            >
              <div class="col-span-12 sm:col-span-3">
                {{ $t("vendings.id") }}
              </div>
              <div class="col-span-12 sm:col-span-4">
                {{ $t("vendings.location") }}
              </div>
              <div class="col-span-12 sm:col-span-3">
                {{ $t("vendings.lastUpdate") }}
              </div>
              <div class="col-span-12 sm:col-span-2">
                {{ $t("vendings.status") }}
              </div>
            </div>

            <div class="space-y-4">
              <div
                v-for="machine in paginatedMachines"
                :key="machine._id"
                class="bg-white rounded-xl p-4 grid grid-cols-1 sm:grid-cols-12 gap-4 items-start sm:items-center hover:shadow-xl transition-all duration-300 shadow-lg border border-gray-100"
              >
                <!-- ID/Nome -->
                <div class="col-span-1 sm:col-span-3 flex items-center gap-3">
                  <div
                    class="text-[#FF5722] bg-[#FF5722] bg-opacity-10 p-2 rounded-lg"
                  >
                    <VendingIcon class="text-xl" />
                  </div>
                  <div class="flex items-center gap-2">
                    <p class="font-medium text-[#4A4453]">
                      {{ machine.vendingName }}
                    </p>
                    <span class="material-icons text-[#FF5722] text-sm"
                      >fingerprint</span
                    >
                  </div>
                </div>

                <!-- Localização -->
                <div
                  class="col-span-1 sm:col-span-4 flex items-center gap-2 text-sm text-[#4A4453]"
                >
                  <span class="material-icons text-[#FF5722] text-sm"
                    >location_on</span
                  >
                  <span class="opacity-80 line-clamp-2">{{
                    `${machine.address.street}, ${machine.address.number} - ${machine.address.neighborhood}, ${machine.address.city} - ${machine.address.state}`
                  }}</span>
                </div>

                <!-- Última Atualização -->
                <div
                  class="col-span-1 sm:col-span-3 flex items-center gap-2 text-sm text-[#4A4453]"
                >
                  <span class="material-icons text-[#FF5722] text-sm"
                    >update</span
                  >
                  <span class="opacity-80"
                    >{{
                      machine.connectionStatus
                        ? $t("vendings.onlineSince")
                        : $t("vendings.offlineSince")
                    }}
                    {{
                      machine.lastConnection
                        ? moment(machine.lastConnection).format(
                            "DD/MM/YY HH:mm:ss"
                          )
                        : ""
                    }}</span
                  >
                </div>

                <!-- Status -->
                <div class="col-span-1 sm:col-span-2">
                  <div
                    :class="[
                      'px-3 py-1.5 rounded-full text-sm flex items-center gap-2 w-fit',
                      machine.connectionStatus
                        ? 'bg-green-100 text-[#4CAF50]'
                        : 'bg-red-100 text-[#F44336]'
                    ]"
                  >
                    <span
                      class="w-2 h-2 rounded-full"
                      :class="
                        machine.connectionStatus
                          ? 'bg-[#4CAF50]'
                          : 'bg-[#F44336]'
                      "
                    ></span>
                    {{
                      machine.connectionStatus
                        ? $t("vendings.filterOnline")
                        : $t("vendings.filterOffline")
                    }}
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Rodapé com Paginação -->
        <div class="border-t border-gray-200 mt-8">
          <Pagination
            v-if="totalPages > 1"
            :current-page="currentPage"
            :total-pages="totalPages"
            @update:page="currentPage = $event"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="js">
import StatsCard from '../common/StatsCard.vue'
import VendingMachineCard from './VendingMachineCard.vue'
import SearchBar from '../common/SearchBar.vue'
import FilterBar from '../common/FilterBar.vue'
import VendingIcon from '../icons/VendingIcon.vue'
import Pagination from '../common/Pagination.vue'


/**
 * @typedef {Object} VendingMachine
 * @property {string} _id
 * @property {string} vendingName
 * @property {Object} address
 * @property {string} lastConnection
 * @property {boolean} connectionStatus
 */

export default {
  name: 'VendingPanel',
  components: {
    StatsCard,
    VendingMachineCard,
    SearchBar,
    FilterBar,
    VendingIcon,
    Pagination
  },

  data() {
    return {
      viewMode: 'grid',
      searchQuery: '',
      filters: {
        status: 'all',
        sort: 'newest'
      },
      currentPage: 1,
      itemsPerPage: 20,
      machines: [],
      isLoading: false,
    }
  },

  computed: {
    filteredMachines() {
      const query = this.searchQuery.toLowerCase()

      // Aplicar filtro de busca
      let filtered = this.machines.filter(machine =>
        machine.vendingName.toLowerCase().includes(query) ||
        machine.address.street.toLowerCase().includes(query) ||
        machine.address.neighborhood.toLowerCase().includes(query)||
        machine.address.city.toLowerCase().includes(query) ||
        machine.address.state.toLowerCase().includes(query)

      )

      // Aplicar filtro de status
      if (this.filters.status !== 'all') {
        const isOnline = this.filters.status === 'online'
        filtered = filtered.filter(machine => machine.connectionStatus === isOnline || (!isOnline && !machine.connectionStatus))
      }

      // Aplicar ordenação por data
      // filtered.sort((a, b) => {
      //   const dateA = new Date(a.lastUpdate.split(' ')[0].split('/').reverse().join('-') + ' ' + a.lastUpdate.split(' ')[1])
      //   const dateB = new Date(b.lastUpdate.split(' ')[0].split('/').reverse().join('-') + ' ' + b.lastUpdate.split(' ')[1])
      //   return this.filters.sort === 'newest' ? dateB.getTime() - dateA.getTime() : dateA.getTime() - dateB.getTime()
      // })


      // Aplicar ordenação por data
      filtered.sort((a, b) => {
        if (!a.lastConnection || !b.lastConnection) {
          return 0; // Se a data de conexão não estiver disponível, não alterar a ordem
        }
        const dateA = this.moment(a.lastConnection);
        const dateB = this.moment(b.lastConnection);
        return this.filters.sort === 'newest' ? dateB.valueOf() - dateA.valueOf() : dateA.valueOf() - dateB.valueOf()
      });


      return filtered
    },

    paginatedMachines() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredMachines.slice(start, end)
    },

    totalPages() {
      return Math.ceil(this.filteredMachines.length / this.itemsPerPage)
    },

    totalMachines() {
      return this.machines.length
    },

    activeMachines() {
      return this.machines.filter(m => m.connectionStatus).length
    },

    inactiveMachines() {
      return this.machines.filter(m => !m.connectionStatus).length
    }
  },

  watch: {
    searchQuery() {
      this.currentPage = 1
    },
    filters: {
      handler() {
        this.currentPage = 1
      },
      deep: true
    }
  },

  methods: {
    getMachines() {
      this.isLoading = true;
      this.$store.dispatch('getVendings')
        .then(response => {
          this.machines = response.data.payload.vendings
        })
        .catch(error => {
          console.error('Erro ao buscar máquinas:', error)
        }).finally(() => {
          this.isLoading = false
        })
    },

    toggleMachineStatus(machine) {
      machine.isOnline = !machine.isOnline
      const now = new Date()
      const day = String(now.getDate()).padStart(2, '0')
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const year = now.getFullYear()
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      machine.lastUpdate = `${day}/${month}/${year} ${hours}:${minutes}`
    },

    showMachineDetails(machine) {
      console.log('Mostrar detalhes da máquina:', machine.id)
    },

  },

  mounted() {
    this.getMachines();
  }
}
</script>
